
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import { isEqual } from 'lodash';
import { isNullOrUndefined } from '@/shared/helpers';
import getTrailersAlreadyAttachedToTruck from '@/shared/mixins/getTrailersAlreadyAttachedToTruck';
import checkTrailerAttachability from '@/shared/mixins/checkTrailerAttachability';
import { ActionTypes } from '@/store/actionTypes';
import { NearbyTrailerResultType } from '@/types/NearbyTrailerResultType';
import Trailer from '@/types/Trailer';
import FbaApiError from '@/types/FbaApiError';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import RouteSummary from '@/types/RouteSummary';
import AlreadyHaveTrailerAttached from '@/components/AlreadyHaveTrailerAttached.vue';
import AttachTrailerConfirmation from '@/components/AttachTrailerConfirmation.vue';
import LocationContent from '@/components/LocationContent.vue';
import FbxButton from '@/components/fbx-button.vue';
import SidebarConfirmation from '@/components/SidebarConfirmation.vue';
import VerifyProximity from '@/components/VerifyProximity.vue';
import ItemNavigator from '@/components/ItemNavigator.vue';
import queries from '@/queries';
import DriverLoaded from '@/components/DriverLoaded.vue';
import ConfirmationOverlay from '@/components/ConfirmationOverlay.vue';
import { CargoStatus } from '@/types/CargoStatus';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';
import Truck from '@/types/Truck';

interface Data {
    additionalTrailerDetails: NearbyTrailerResult[] | null;
    currentAttachedTrailers: Array<Trailer>;
    showConfirmNewNavigation: boolean;
    detachModalHidden: boolean;
    didSelectNavDestination: boolean;
    displayedResult: NearbyTrailerResult;
    proximityWasVerifiedUsingQrScan: boolean;
    showConfirm: boolean;
    showLoading: boolean;
    verifyProximity: boolean;
    showLoadedTrailerModal: boolean;
    showCargoSensorIssueModal: boolean;
    displayLoadedConfirmation: boolean;
    fromReservation: boolean;
}

export default defineComponent({
    components: {
        AlreadyHaveTrailerAttached,
        AttachTrailerConfirmation,
        FbxButton,
        ItemNavigator,
        LocationContent,
        SidebarConfirmation,
        VerifyProximity,
        ConfirmationOverlay,
        DriverLoaded,
    },
    props: {
        selectedResult: {
            type: NearbyTrailerResult,
            required: true,
        },
    },
    emits: [
        'displayCustomerTrailers',
        'backToListView',
        'setSelectedResult',
        'navigationSent',
    ],
    mixins: [checkTrailerAttachability, getTrailersAlreadyAttachedToTruck],
    data(): Data {
        return {
            additionalTrailerDetails: null,
            currentAttachedTrailers: [],
            detachModalHidden: false,
            didSelectNavDestination: false,
            displayedResult: this.selectedResult,
            proximityWasVerifiedUsingQrScan: false,
            showConfirmNewNavigation: false,
            showConfirm: false,
            showLoading: false,
            verifyProximity: false,
            showLoadedTrailerModal: false,
            showCargoSensorIssueModal: false,
            displayLoadedConfirmation: false,
            fromReservation: false,
        };
    },
    computed: {
        ...mapGetters({
            currentTrailerReservation: 'getCurrentTrailerReservation',
            driver: 'getDriver',
            trailerAssociationsEnabled: 'trailerAssociationsEnabled',
            bestAvailableLocation: 'getPinnedBestAvailableLocation',
            getTruck: 'getTruck',
        }),
        loadedModalTrailerCompany(): string | null {
            if (this.fromReservation === true) return this.trailerCompany;
            else
                return (
                    this.currentAttachedTrailers[0]?.company ??
                    this.driver.company
                );
        },
        loadedModalTrailerNumber(): string | null {
            if (this.fromReservation === true) return this.trailerNumber;
            else return this.currentAttachedTrailers[0]?.trailerNumber ?? null;
        },
        results(): NearbyTrailerResult[] {
            return this.$store.getters.getNearbyTrailerResults === undefined
                ? []
                : this.$store.getters.getNearbyTrailerResults;
        },
        getTrailer() {
            if (this.isCustomerLocation === true) return undefined;
            return this.selectedResult.trailers[0];
        },
        driverInRangeOfTrailer() {
            return (
                this.selectedResult.distance <= 1.0 &&
                this.isCustomerLocation === false
            );
        },
        currentIndex() {
            return this.results.findIndex((result) =>
                isEqual(result, this.displayedResult)
            );
        },
        isCustomerLocation(): boolean {
            return (
                this.displayedResult?.type ===
                NearbyTrailerResultType.customerLocation
            );
        },
        isReserved(): boolean {
            return (
                !isNullOrUndefined(this.displayedResult?.trailers) &&
                this.displayedResult.trailers.findIndex(
                    (item) => item?.reservation?.isActive
                ) > -1
            );
        },
        locationTitle(): string {
            if (this.isCustomerLocation) {
                return this.displayedResult.locationName?.trim().toLowerCase();
            } else {
                return `${this.isReserved ? 'Your ' : ''}Trailer #${
                    this.trailerNumber
                }`;
            }
        },
        distance(): string {
            return this.displayedResult.distance.toFixed(1);
        },
        travelDistance(): number {
            if (this.displayedResult.route == undefined) return 0;

            return (this.displayedResult.route as RouteSummary).lengthInMiles();
        },
        travelTime(): string {
            return `${
                isNullOrUndefined(this.displayedResult?.route?.duration)
                    ? '?'
                    : parseInt(
                          Math.ceil(
                              (this.displayedResult.route?.duration ?? 0) / 60
                          ).toString()
                      )
            }`;
        },
        isCurrentNavigationTrailer(): boolean {
            return (
                this.currentTrailerReservation?.trailerId?.split('|')[0] ===
                this.trailerNumber
            );
        },
        trailerNumber(): string {
            return this.displayedResult?.trailers[0]?.trailerNumber || '';
        },
        trailerCompany(): string {
            return this.displayedResult?.trailers[0]?.company || '';
        },
    },
    methods: {
        onDisplayCustomerTrailers() {
            this.$emit('displayCustomerTrailers', true);
        },
        hideModal() {
            this.showLoadedTrailerModal = false;
            this.fromReservation = false;
        },
        async checkAttachabilityForDriver() {
            return await this.checkTrailerAttachability({
                trailerNumber: this.trailerNumber,
                trailerCompany: this.trailerCompany,
                driverId: this.driver.id,
            });
        },
        cancelConfirm(): void {
            this.showConfirm = false;
        },
        hideTrailerAlreadyAttachedModal() {
            this.detachModalHidden = true;
        },
        hideCargoSensorIssueModal() {
            this.showCargoSensorIssueModal = false;
        },
        routeToTrailerAssessment(): void {
            const trailer = this.selectedResult?.trailers[0];

            if (!isNullOrUndefined(trailer) && trailer instanceof Trailer) {
                if (this.proximityWasVerifiedUsingQrScan === false) {
                    // Verifying via a QR scan already populates the selected trailer
                    this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                        trailer,
                        trailerSelectionMethod: TrailerSelectionMethod.find,
                    });
                }

                this.$router.push({
                    name: 'TrailerAssessment',
                    params: { trailerNumber: trailer.trailerNumber },
                });
            }
        },
        backToListView(): void {
            this.$emit('backToListView');
        },
        cycleLeft(): void {
            let newIndex = this.currentIndex - 1;
            if (newIndex < 0) newIndex = this.results.length - 1;
            this.displayedResult = this.results[newIndex];
            this.proximityWasVerifiedUsingQrScan = false;
            this.$emit('setSelectedResult', this.displayedResult);
        },
        cycleRight(): void {
            const newIndex = (this.currentIndex + 1) % this.results.length;
            this.displayedResult = this.results[newIndex];
            this.proximityWasVerifiedUsingQrScan = false;
            this.$emit('setSelectedResult', this.displayedResult);
        },
        async checkCargoSensorAndShowModal(): Promise<void> {
            await this.$store.dispatch(ActionTypes.FETCH_DRIVER, {
                company: this.driver.company,
                driverCode: this.driver.driverCode,
            });
            if (this.driver.isLoaded === true) {
                this.fromReservation = true;
                this.showLoadedTrailerModal = true;
                return;
            }
            if (
                this.displayedResult.type ===
                NearbyTrailerResultType.standaloneTrailer
            ) {
                if (
                    this.displayedResult.trailers[0].cargoStatus ===
                        CargoStatus.NO_SENSOR ||
                    this.displayedResult.trailers[0].cargoStatus ===
                        CargoStatus.SENSOR_ERROR
                ) {
                    this.showCargoSensorIssueModal = true;
                    return;
                } else await this.checkCurrentNavigationTask();
            } else await this.checkCurrentNavigationTask();
        },
        async checkCurrentNavigationTask(): Promise<void> {
            if (!isNullOrUndefined(this.currentTrailerReservation)) {
                if (this.isCurrentNavigationTrailer) return;

                this.showConfirmNewNavigation = true;
                this.hideCargoSensorIssueModal();
                return;
            }
            this.hideCargoSensorIssueModal();
            await this.sendNavigationTask();
        },
        createNavigationTaskPayload(isManual = false) {
            const estimatedMinsToDestination = parseInt(this.travelTime);
            let minutesReserve = estimatedMinsToDestination * 2 || 15;
            if (minutesReserve < 15) minutesReserve = 15;

            let destination = this.displayedResult.location ?? '';
            if (this.additionalTrailerDetails) {
                destination = this.additionalTrailerDetails[0].location;
            }

            return {
                name: this.locationTitle,
                city: this.displayedResult.locationAddress?.city ?? '',
                state: this.displayedResult.locationAddress?.state ?? '',
                zip: this.displayedResult.locationAddress?.zip ?? '',
                destination,
                minutesToDestination: estimatedMinsToDestination || undefined,
                milesToDestination: this.travelDistance,
                minutesToReserveFor: minutesReserve,
                trailerId:
                    this.selectedResult.trailers.length > 0
                        ? this.selectedResult.trailers[0].id
                        : undefined,
                customerId: this.selectedResult.customerId,
                locationId: this.selectedResult.locationId,
                isManualSwap: isManual,
                trailerNumber:
                    this.selectedResult.trailers.length > 0
                        ? this.selectedResult.trailers[0].trailerNumber
                        : undefined,
            };
        },
        async sendNavigationTask(isManual = false): Promise<void> {
            this.showLoading = true;
            const isTrailerAvailable = await this.checkAttachabilityForDriver();
            if (isTrailerAvailable === false) {
                this.$router.push({
                    name: 'TrailerUnavailable',
                    query: {
                        trailerNumber: this.trailerNumber,
                        trailerCompany: this.trailerCompany,
                    },
                });
                return;
            } else {
                this.didSelectNavDestination = true;

                if (
                    this.displayedResult.type ===
                    NearbyTrailerResultType.customerLocation
                ) {
                    const result =
                        await this.getTrailerLocationWithinCustomerLot();

                    if (result) {
                        this.additionalTrailerDetails = result;
                    }
                }

                const navRequestPayload =
                    this.createNavigationTaskPayload(isManual);

                const response = await this.$store.dispatch(
                    ActionTypes.CREATE_NAVIGATION_DESTINATION,
                    navRequestPayload
                );
                if (
                    response instanceof FbaApiError ||
                    response.didSucceed === false
                ) {
                    this.$router.push({
                        name: 'TrailerUnavailable',
                        query: {
                            trailerNumber: this.trailerNumber,
                            trailerCompany: this.trailerCompany,
                        },
                    });
                    return;
                } else {
                    this.$emit('navigationSent');
                    this.didSelectNavDestination = false;
                }
            }
            this.showLoading = false;
        },
        async getTrailerLocationWithinCustomerLot(): Promise<
            NearbyTrailerResult[] | undefined
        > {
            const [_, company] = this.displayedResult.customerId.split('|');
            const trailersAtCustomerLocation =
                await queries.getTrailersAtCustomer(
                    company,
                    this.displayedResult.customerId,
                    this.bestAvailableLocation,
                    (this.truck as Truck).id
                );

            if (trailersAtCustomerLocation instanceof FbaApiError) {
                console.error(trailersAtCustomerLocation as FbaApiError);
                return;
            }

            return trailersAtCustomerLocation.filter(
                (trailer) =>
                    trailer.trailers[0].trailerNumber ===
                    this.displayedResult.trailers[0].trailerNumber
            );
        },
        declineNavigationChange() {
            this.showConfirmNewNavigation = false;
        },
        detachErrorOccurred() {
            this.currentAttachedTrailers = [];
            this.showConfirm = false;
        },
        async plateVinProximityVerifiedAttach() {
            const isTrailerAvailable = await this.checkAttachabilityForDriver();
            if (isTrailerAvailable === false) {
                this.$router.push({
                    name: 'TrailerUnavailable',
                    query: {
                        trailerNumber: this.trailerNumber,
                        trailerCompany: this.trailerCompany,
                    },
                });
                this.currentAttachedTrailers = [];
                return;
            }
            this.proximityWasVerifiedUsingQrScan = false;

            this.routeToTrailerAssessment();
            this.verifyProximity = false;
        },
        async qrScanProximityVerifiedAttach() {
            await this.confirmTrailerAttach();
            this.proximityWasVerifiedUsingQrScan = true;
            this.verifyProximity = false;
        },
        async confirmTrailerAttach(detachSucceeded?: boolean): Promise<void> {
            if (detachSucceeded === false) {
                this.detachErrorOccurred();
                return;
            }

            const isTrailerAvailable = await this.checkAttachabilityForDriver();
            if (isTrailerAvailable === false) {
                this.$router.push({
                    name: 'TrailerUnavailable',
                    query: {
                        trailerNumber: this.trailerNumber,
                        trailerCompany: this.trailerCompany,
                    },
                });
                this.currentAttachedTrailers = [];
                return;
            }
            this.showConfirm = true;
            this.currentAttachedTrailers = [];
        },
        async fetchDriver() {
            await this.$store.dispatch(ActionTypes.FETCH_DRIVER, {
                company: this.driver.company,
                driverCode: this.driver.driverCode,
            });
        },
        async setupTrailerAttach(detachSucceeded?: boolean): Promise<void> {
            if (detachSucceeded === false) {
                this.detachErrorOccurred();
                return;
            }

            const attachedTrailers =
                await this.getTrailersAlreadyAttachedToTruck();

            await this.fetchDriver();

            if (this.driver?.isLoaded === true) {
                // Run attachability before showing the loaded screen so the
                // attempted attachment is persisted for this route as well.
                await this.checkAttachabilityForDriver();
                this.showLoadedTrailerModal = true;
                this.detachModalHidden = true;
                return;
            }

            if (
                !isNullOrUndefined(attachedTrailers) &&
                Array.isArray(attachedTrailers) &&
                !(typeof detachSucceeded === 'boolean' && detachSucceeded)
            ) {
                this.currentAttachedTrailers = attachedTrailers;
                this.detachModalHidden = false;
                return;
            }

            if (this.driverInRangeOfTrailer === true) {
                await this.confirmTrailerAttach();
            } else {
                this.validateTrailerProximity();
            }
        },
        validateTrailerProximity() {
            this.hideTrailerAlreadyAttachedModal();
            this.verifyProximity = true;
        },
    },
    watch: {
        selectedResult(val): void {
            if (!isNullOrUndefined(val))
                this.displayedResult = this.selectedResult;
        },
    },
});
